import {
    Box,
    Button,
    ButtonGroup,
    Collapse,
    Flex,
    HStack,
    IconButton,
    Menu,
    MenuButton,
    MenuDivider,
    MenuItem,
    MenuList,
    Select,
    Spinner,
    Text,
    useDisclosure,
    useToast,
} from "@chakra-ui/react";
import {
    IconChevronDown,
    IconFileCode,
    IconFileSpreadsheet,
    IconFileText,
    IconFilter,
    IconBookmark,
    IconBookmarkFilled,
    IconBookmarkPlus,
    IconSettings,
    IconArrowsLeftRight,
    IconTrash,
    IconX,
} from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { triggerDownloadPrompt } from "@am-tax/fe-core";
import type { RowSelectionState } from "@tanstack/react-table";
import { useQueryClient } from "@tanstack/react-query";
import { useClientEntities } from "../../api/hooks/useClientEntities";
import { buildRequestBody, useCandidatesPage } from "../../api/hooks/useCandidatesPage";
import { type CandidateExportFormat, useCandidatesExport, type ExportFilterDescription } from "../../api/hooks/useCandidatesExport";
import { useBulkOperation } from "../../api/hooks/useBulkOperation";
import { queryKeys } from "../../api/queryKeys";
import { axiosInstance } from "../../utils";
import { CandidatesFilters } from "./CandidatesFilters";
import { CandidatesTable } from "./CandidatesTable";
import { CandidateDrawer } from "./CandidateDrawer";
import { EntityScopePicker } from "./EntityScopePicker";
import { useCandidatesUrlState } from "./useCandidatesUrlState";
import { describeColumnFilters } from "./filterDescriptions";
import { CandidatesPresetManager } from "./CandidatesPresetManager";
import { SavePresetModal } from "./SavePresetModal";
import {
    type CandidateGridPreset,
    parsePresetData,
    useCandidateGridPresets,
    useCreateCandidateGridPreset,
    type PresetData,
} from "../../api/candidateGridPresets";
import { BulkProgressModal } from "./BulkProgressModal";
import { BulkMoveModal } from "./BulkMoveModal";
import { ConfirmationDialog } from "../../components/ConfirmationDialog/ConfirmationDialog";
import type { EntityResponse } from "../../api/models/EntityResponse";

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

function findEntityByIdRecursive(entities: EntityResponse[], id: string): EntityResponse | undefined {
    for (const entity of entities) {
        if (entity.id === id) return entity;
        if (entity.children?.length) {
            const found = findEntityByIdRecursive(entity.children, id);
            if (found) return found;
        }
    }
    return undefined;
}

export function CandidatesPage() {
    const { cId } = useParams<{ cId: string }>();
    const clientId = cId!;

    const {
        entityId,
        page,
        pageSize,
        sorting,
        columnFilters,
        candidateId,
        candidateEntityId,
        setEntityId,
        setPage,
        setPageSize,
        setSorting,
        setColumnFilters,
        applyFilterState,
        setCandidateId,
        clearFilters,
    } = useCandidatesUrlState();

    const { data, isLoading, isFetching } = useCandidatesPage(clientId, entityId, page, pageSize, sorting, columnFilters);
    const { data: entities, isLoading: entitiesLoading } = useClientEntities(clientId);

    const { isOpen: filtersOpen, onToggle: toggleFilters } = useDisclosure({ defaultIsOpen: false });
    const { isOpen: presetManagerOpen, onOpen: openPresetManager, onClose: closePresetManager } = useDisclosure();
    const { isOpen: savePresetOpen, onOpen: openSavePreset, onClose: closeSavePreset } = useDisclosure();
    const toast = useToast();
    const exportCandidates = useCandidatesExport();
    const [isExporting, setIsExporting] = useState(false);

    const presetsQuery = useCandidateGridPresets(clientId);
    const createPreset = useCreateCandidateGridPreset(clientId);

    // Bulk operations
    const queryClient = useQueryClient();
    const axios = axiosInstance();
    const bulk = useBulkOperation();
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    const [progressModalOpen, setProgressModalOpen] = useState(false);
    const [bulkOperationLabel, setBulkOperationLabel] = useState("");
    const [moveModalOpen, setMoveModalOpen] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

    // Clear selection when page or page size changes
    useEffect(() => {
        setRowSelection({});
    }, [page, pageSize]);

    // Auto-apply default preset on first load if no filters are active
    const defaultAppliedRef = useRef(false);
    useEffect(() => {
        if (defaultAppliedRef.current) return;
        if (presetsQuery.data == null) return;
        if (columnFilters.length > 0 || sorting.length > 0) {
            defaultAppliedRef.current = true;
            return;
        }
        defaultAppliedRef.current = true;
        const defaultPreset = presetsQuery.data.find(p => p.isDefault);
        if (!defaultPreset) return;
        try {
            const presetData: PresetData = JSON.parse(defaultPreset.presetJson);
            if (presetData.columnFilters.length > 0 || presetData.sorting.length > 0) {
                applyFilterState(presetData.columnFilters, presetData.sorting);
                toast({
                    id: "default-preset-applied",
                    title: "Default filter applied",
                    description: `"${defaultPreset.name}" was automatically applied. Use the Filters panel or clear filters to change your view.`,
                    status: "info",
                    duration: 6000,
                    isClosable: true,
                    position: "top",
                });
            }
        } catch {
            // ignore malformed preset JSON
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [presetsQuery.data]);

    const totalCount = data?.totalCount ?? 0;
    const totalPages = data?.totalPages ?? 0;
    const items = data?.items ?? [];

    const selectedCandidates = items.filter(c => rowSelection[c.id] === true);
    const selectedCount = selectedCandidates.length;

    const activeFilterCount = columnFilters.length;

    /** Builds humanized filter descriptions from the current column filter state. */
    function buildFilterDescriptions(): ExportFilterDescription[] {
        const descriptions: ExportFilterDescription[] = describeColumnFilters(columnFilters);

        // Entity from URL state (entity scope selector, separate from column filters)
        if (entityId) {
            const entityName = findEntityByIdRecursive(entities ?? [], entityId)?.name;
            if (entityName) {
                descriptions.unshift({ label: "Entity (scope)", value: entityName });
            }
        }

        return descriptions;
    }

    async function handleBulkDelete() {
        setDeleteConfirmOpen(false);
        setBulkOperationLabel(`Deleting ${selectedCount} Candidate${selectedCount !== 1 ? "s" : ""}…`);
        setProgressModalOpen(true);
        const ops = selectedCandidates.map(c => ({
            label: [c.firstName, c.lastName].filter(Boolean).join(" ") || c.id,
            fn: () => axios.delete(`api/clients/${clientId}/entities/${c.entityId}/candidates/${c.id}/DeleteEntityCandidate`).then(() => {}),
        }));
        await bulk.run(ops);
    }

    async function handleBulkMove(targetEntityId: string) {
        setBulkOperationLabel(`Moving ${selectedCount} Candidate${selectedCount !== 1 ? "s" : ""}…`);
        setProgressModalOpen(true);
        const ops = selectedCandidates.map(c => ({
            label: [c.firstName, c.lastName].filter(Boolean).join(" ") || c.id,
            fn: () =>
                axios
                    .post(`api/clients/${clientId}/entities/${c.entityId}/candidates/${c.id}/move`, {
                        targetEntityId,
                    })
                    .then(() => {}),
        }));
        await bulk.run(ops);
    }

    function handleBulkDone() {
        setProgressModalOpen(false);
        bulk.reset();
        setRowSelection({});
        queryClient.invalidateQueries(queryKeys.candidatesPage.search._def);
    }

    async function handleSaveCurrentAsPreset(name: string, isDefault: boolean) {
        const presetData: PresetData = { columnFilters, sorting };
        await createPreset.mutateAsync({
            name,
            presetJson: JSON.stringify(presetData),
            isDefault,
        });
        closeSavePreset();
    }

    function handleApplyPreset(presetData: PresetData) {
        applyFilterState(presetData.columnFilters, presetData.sorting);
        if (!filtersOpen && presetData.columnFilters.length > 0) {
            toggleFilters();
        }
    }

    /** Applies a saved preset directly from the quick-apply menu (parses its stored JSON first). */
    function handleQuickApplyPreset(preset: CandidateGridPreset) {
        const presetData = parsePresetData(preset.presetJson);
        if (!presetData) {
            toast({ status: "error", title: "Invalid preset data", isClosable: true });
            return;
        }
        handleApplyPreset(presetData);
    }

    async function handleExport(format: CandidateExportFormat) {
        setIsExporting(true);
        try {
            const { filters, qualificationFilter, qualificationFilterMode } = buildRequestBody(sorting, columnFilters, 1, 1);
            const blob = await exportCandidates(clientId, {
                entityId,
                sort: sorting,
                filters,
                qualificationFilter,
                qualificationFilterMode,
                format,
                filterDescriptions: buildFilterDescriptions(),
            });
            const extensions: Record<CandidateExportFormat, string> = { xlsx: ".xlsx", csv: ".csv", json: ".json" };
            const date = new Date().toISOString().slice(0, 10).replace(/-/g, "");
            triggerDownloadPrompt(blob, `Candidates_Export_${date}${extensions[format]}`);
        } catch {
            toast({
                title: "Export failed",
                description: "An error occurred while exporting candidates. Please try again.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsExporting(false);
        }
    }

    return (
        <Box height="100%" display="flex" flexDirection="column" overflow="hidden">
            {/* Toolbar — doubles as bulk action bar when rows are selected */}
            <Flex
                px={4}
                py={3}
                align="center"
                justify="space-between"
                borderBottomWidth={1}
                flexShrink={0}
                bg={selectedCount > 0 ? "blue.50" : undefined}
                borderColor={selectedCount > 0 ? "blue.200" : undefined}
                _dark={selectedCount > 0 ? { bg: "blue.900", borderColor: "blue.700" } : undefined}
                transition="background-color 0.15s"
            >
                <HStack spacing={3} align="center">
                    <EntityScopePicker entities={entities} selectedId={entityId} isLoading={entitiesLoading} onChange={setEntityId} />
                    {selectedCount > 0 && (
                        <>
                            <Text fontSize="sm" fontWeight="semibold" color="blue.700" _dark={{ color: "blue.200" }}>
                                {selectedCount} selected
                            </Text>
                            <Button size="xs" variant="ghost" color="gray.500" leftIcon={<IconX size="0.75rem" />} onClick={() => setRowSelection({})}>
                                Clear
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                colorScheme="blue"
                                leftIcon={<IconArrowsLeftRight size="0.9rem" />}
                                onClick={() => setMoveModalOpen(true)}
                            >
                                Move
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                colorScheme="red"
                                leftIcon={<IconTrash size="0.9rem" />}
                                onClick={() => setDeleteConfirmOpen(true)}
                            >
                                Delete
                            </Button>
                        </>
                    )}
                </HStack>

                <HStack spacing={2}>
                    <Menu>
                        <MenuButton
                            as={Button}
                            size="sm"
                            variant="outline"
                            rightIcon={<IconChevronDown size="0.85rem" />}
                            isLoading={isExporting}
                            loadingText="Exporting…"
                        >
                            Export
                        </MenuButton>
                        <MenuList>
                            <MenuItem icon={<IconFileSpreadsheet size="1rem" />} onClick={() => handleExport("xlsx")}>
                                Excel (.xlsx)
                            </MenuItem>
                            <MenuDivider />
                            <MenuItem icon={<IconFileText size="1rem" />} onClick={() => handleExport("csv")}>
                                CSV (.csv)
                            </MenuItem>
                            <MenuItem icon={<IconFileCode size="1rem" />} onClick={() => handleExport("json")}>
                                JSON (.json)
                            </MenuItem>
                        </MenuList>
                    </Menu>
                    <ButtonGroup size="sm" isAttached variant={filtersOpen ? "solid" : "outline"} colorScheme={activeFilterCount > 0 ? "blue" : "gray"}>
                        <Button leftIcon={<IconFilter size="0.9rem" />} onClick={toggleFilters}>
                            Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
                        </Button>
                        <Menu placement="bottom-end">
                            <MenuButton as={IconButton} aria-label="Filter presets" icon={<IconBookmark size="0.9rem" />} borderLeftWidth={1} />
                            <MenuList maxW="16rem">
                                {presetsQuery.data && presetsQuery.data.length > 0 && (
                                    <>
                                        {presetsQuery.data.map(preset => (
                                            <MenuItem
                                                key={preset.id}
                                                icon={preset.isDefault ? <IconBookmarkFilled size="1rem" /> : <IconBookmark size="1rem" />}
                                                onClick={() => handleQuickApplyPreset(preset)}
                                            >
                                                <Text as="span" noOfLines={1}>
                                                    {preset.name}
                                                    {preset.isDefault && (
                                                        <Text as="span" color="gray.500" fontSize="xs" ml={1}>
                                                            (Default)
                                                        </Text>
                                                    )}
                                                </Text>
                                            </MenuItem>
                                        ))}
                                        <MenuDivider />
                                    </>
                                )}
                                <MenuItem icon={<IconSettings size="1rem" />} onClick={openPresetManager}>
                                    Manage Presets…
                                </MenuItem>
                                <MenuItem icon={<IconBookmarkPlus size="1rem" />} onClick={openSavePreset}>
                                    Save Current as Preset…
                                </MenuItem>
                            </MenuList>
                        </Menu>
                    </ButtonGroup>
                </HStack>
            </Flex>

            {/* Collapsible filter panel */}
            <Collapse in={filtersOpen} animateOpacity>
                <Box px={4} pt={3} pb={2} borderBottomWidth={1} flexShrink={0}>
                    <CandidatesFilters columnFilters={columnFilters} onFiltersChange={setColumnFilters} onClearAll={clearFilters} />
                </Box>
            </Collapse>

            {/* Table — grows to fill space */}
            <Box flex={1} overflow="auto">
                <CandidatesTable
                    data={items}
                    totalCount={totalCount}
                    isLoading={isLoading}
                    isFetching={isFetching}
                    sorting={sorting}
                    columnFilters={columnFilters}
                    rowSelection={rowSelection}
                    onSortingChange={setSorting}
                    onColumnFiltersChange={setColumnFilters}
                    onRowSelectionChange={setRowSelection}
                    onRowClick={row => setCandidateId(row.id, row.entityId)}
                    selectedCandidateId={candidateId}
                />
            </Box>

            {/* Pagination footer */}
            <Flex px={4} py={2} align="center" justify="space-between" borderTopWidth={1} flexShrink={0} bg="white" _dark={{ bg: "gray.800" }}>
                <HStack spacing={2}>
                    <Text fontSize="sm" color="gray.500">
                        Rows per page:
                    </Text>
                    <Select
                        size="xs"
                        width="70px"
                        value={pageSize}
                        title="Rows per page"
                        aria-label="Rows per page"
                        onChange={e => setPageSize(Number(e.target.value))}
                    >
                        {PAGE_SIZE_OPTIONS.map(s => (
                            <option key={s} value={s}>
                                {s}
                            </option>
                        ))}
                    </Select>
                </HStack>

                <HStack spacing={2} color="gray.500">
                    {isFetching && <Spinner size="xs" />}
                    {!isLoading && (
                        <Text fontSize="sm">
                            {totalCount.toLocaleString()} candidate{totalCount !== 1 ? "s" : ""}
                        </Text>
                    )}
                    {!isLoading && totalPages > 1 && (
                        <>
                            <Text fontSize="sm" color="gray.300">
                                ·
                            </Text>
                            <Text fontSize="sm">
                                Page {page} of {totalPages}
                            </Text>
                        </>
                    )}
                </HStack>

                <HStack spacing={1}>
                    <Button size="xs" isDisabled={page <= 1} onClick={() => setPage(1)} variant="ghost">
                        «
                    </Button>
                    <Button size="xs" isDisabled={page <= 1} onClick={() => setPage(page - 1)} variant="ghost">
                        ‹
                    </Button>
                    <Button size="xs" isDisabled={page >= totalPages} onClick={() => setPage(page + 1)} variant="ghost">
                        ›
                    </Button>
                    <Button size="xs" isDisabled={page >= totalPages} onClick={() => setPage(totalPages)} variant="ghost">
                        »
                    </Button>
                </HStack>
            </Flex>

            {/* Candidate detail drawer — full-size, replaces quick-view modal */}
            <CandidateDrawer clientId={clientId} entityId={candidateEntityId} candidateId={candidateId} onClose={() => setCandidateId(undefined)} />

            <CandidatesPresetManager
                isOpen={presetManagerOpen}
                onClose={closePresetManager}
                clientId={clientId}
                onApplyPreset={handleApplyPreset}
                onSaveCurrentAsPreset={openSavePreset}
            />
            <SavePresetModal isOpen={savePresetOpen} onClose={closeSavePreset} onSave={handleSaveCurrentAsPreset} isSaving={createPreset.isLoading} />

            {/* Bulk action modals */}
            <BulkMoveModal
                isOpen={moveModalOpen}
                onClose={() => setMoveModalOpen(false)}
                clientId={clientId}
                selectedCount={selectedCount}
                onConfirm={handleBulkMove}
            />

            <BulkProgressModal isOpen={progressModalOpen} operationLabel={bulkOperationLabel} state={bulk.state} onDone={handleBulkDone} />

            <ConfirmationDialog
                isOpen={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
                onConfirm={handleBulkDelete}
                title={`Delete ${selectedCount} Candidate${selectedCount !== 1 ? "s" : ""}?`}
                confirmLabel="Delete"
                confirmColorScheme="red"
            >
                This will soft-delete {selectedCount} candidate{selectedCount !== 1 ? "s" : ""}. This action can be undone by an administrator.
            </ConfirmationDialog>
        </Box>
    );
}
